import React, { useState } from 'react';
import { StickyDreamSummary } from './sections/StickyDreamSummary';
import { PrimaryRulesSection } from './sections/PrimaryRulesSection';
import { SymbolicMeaningsSection } from './sections/SymbolicMeaningsSection';
import { TwoDRitualRecommendationsSection } from './sections/TwoDRitualRecommendationsSection';
import type { AnalysisResults } from '../types/frontend';

interface ResultsViewProps {
  results: AnalysisResults;
  onReset: () => void;
}

interface SelectedItems {
  rules: Set<string>;
  symbols: Set<string>;
  rituals: Set<string>;
}

export const ResultsView: React.FC<ResultsViewProps> = ({ results, onReset }) => {
  const [selectedItems, setSelectedItems] = useState<SelectedItems>({
    rules: new Set(),
    symbols: new Set(),
    rituals: new Set(),
  });

  const toggleSelection = (type: keyof SelectedItems, id: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev[type]);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return { ...prev, [type]: newSet };
    });
  };
  return (
    <div className="results-container" data-testid="results-view">
      {/* Header with reset button */}
      <div
        className="results-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-8)',
          padding: 'var(--space-5)',
          background: 'var(--papyrus-white)',
          border: '1px solid var(--stone-dark)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.06)',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <h2
          style={{
            fontSize: 'var(--text-xl)',
            fontWeight: 600,
            color: 'var(--charcoal)',
            margin: 0,
            minWidth: 'fit-content',
          }}
        >
          Analysis Results
        </h2>
        <button
          onClick={onReset}
          data-testid="reset-button"
          className="reset-button"
          style={{
            background: 'transparent',
            border: '1px solid var(--stone-dark)',
            padding: 'var(--space-3) var(--space-4)',
            cursor: 'pointer',
            color: 'var(--soot)',
            fontWeight: 500,
            fontSize: 'var(--text-sm)',
            transition: 'background 0.2s ease',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'var(--stone-light)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          ← New Dream
        </button>
      </div>

      {/* Sticky Dream Summary */}
      <StickyDreamSummary
        originalDream={results.originalDream}
        sentimentAnalysis={results.sentimentAnalysis}
        selectedItems={selectedItems}
        results={results}
      />

      {/* Truncation Warning */}
      {results.originalDream.truncated && results.originalDream.truncationWarning && (
        <div
          style={{
            background: 'rgba(212, 165, 116, 0.08)',
            border: '1px solid var(--sun-gold)',
            padding: 'var(--space-4)',
            marginBottom: 'var(--space-6)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--space-3)',
            boxShadow: '0 1px 6px rgba(0, 0, 0, 0.06)',
          }}
        >
          <div>
            <div
              style={{
                fontWeight: 500,
                color: 'var(--cedar-brown)',
                marginBottom: 'var(--space-1)',
                fontSize: 'var(--text-sm)',
              }}
            >
              ⚠ Dream Text Truncated
            </div>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--soot)' }}>
              {results.originalDream.truncationWarning}
            </div>
          </div>
        </div>
      )}

      <PrimaryRulesSection
        rules={results.primaryRules}
        selectedIds={selectedItems.rules}
        onToggleSelection={(id) => toggleSelection('rules', id)}
      />

      {/* 2. Symbolic Meanings */}
      <SymbolicMeaningsSection 
        entities={results.symbolicMeanings}
        selectedIds={selectedItems.symbols}
        onToggleSelection={(id) => toggleSelection('symbols', id)}
      />

      {/* 3. Ritual Recommendations */}
      {results.ritualRecommendations && results.ritualRecommendations.length > 0 && (
        <TwoDRitualRecommendationsSection
          ritualRecommendations={results.ritualRecommendations}
          aggregateSentiment={results.sentimentAnalysis}
          selectedIds={selectedItems.rituals}
          onToggleSelection={(id) => toggleSelection('rituals', id)}
        />
      )}
    </div>
  );
};
